import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Tag, ArrowRight, Info, Shield, Target, TrendingDown, Clock, AlertTriangle, Crown } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { fbTrack } from '../utils/pixel';

const RULES_INFO = {
  '1-step': { label: '1-Step Challenge', color: '#00FF94', rules: [
    { icon: <Target className="w-4 h-4" />, text: 'Profit Target: 9%' },
    { icon: <TrendingDown className="w-4 h-4" />, text: 'Max Drawdown: 6%' },
    { icon: <AlertTriangle className="w-4 h-4" />, text: 'Daily Loss Limit: 3%' },
    { icon: <Clock className="w-4 h-4" />, text: 'Min Trading Days: 3' },
    { icon: <Shield className="w-4 h-4" />, text: 'Leverage: 1:100 | No Time Limit' },
  ]},
  '2-step': { label: '2-Step Challenge', color: '#0099FF', rules: [
    { icon: <Target className="w-4 h-4" />, text: 'Phase 1: 6% Target | Phase 2: 6% Target' },
    { icon: <TrendingDown className="w-4 h-4" />, text: 'Max Drawdown: 6%' },
    { icon: <AlertTriangle className="w-4 h-4" />, text: 'Daily Loss Limit: 3%' },
    { icon: <Clock className="w-4 h-4" />, text: 'Min 3 Trading Days per Phase' },
    { icon: <Shield className="w-4 h-4" />, text: 'Leverage: 1:100 | Consistency Rule: 30%' },
  ]},
  'instant': { label: 'Instant Funding', color: '#7B61FF', rules: [
    { icon: <Target className="w-4 h-4" />, text: 'Profit Split: 80%' },
    { icon: <TrendingDown className="w-4 h-4" />, text: 'Max Drawdown: 7%' },
    { icon: <AlertTriangle className="w-4 h-4" />, text: 'Daily Loss Limit: 4%' },
    { icon: <Shield className="w-4 h-4" />, text: 'Consistency Rule: 30% | 1% Floating Rule' },
  ]},
};

const Pricing = () => {
  const [challengeType, setChallengeType] = useState('1-step');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      const { data } = await api.get('/plans');
      setPlans(data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const { data } = await api.post('/coupons/validate', { code: couponCode });
      setAppliedCoupon(data);
      toast.success(`Coupon applied: ${data.description}`);
    } catch (error) {
      toast.error('Invalid or expired coupon code');
      setAppliedCoupon(null);
    }
  };

  const filteredPlans = plans.filter(p => p.challenge_type === challengeType);
  const getDiscountedPrice = (price) => appliedCoupon ? Math.ceil(price * (100 - appliedCoupon.discount_pct) / 100) : price;

  const handleSelectPlan = (planId) => {
    if (!user || user === false) { navigate('/auth'); return; }
    const plan = plans.find(p => p.id === planId);
    const value = plan ? getDiscountedPrice(plan.price) : 0;
    fbTrack('InitiateCheckout', {
      content_ids: [planId],
      content_name: plan?.name || planId,
      content_type: 'product',
      value,
      currency: 'USD',
    });
    const couponParam = appliedCoupon ? `?coupon=${appliedCoupon.code}` : '';
    navigate(`/checkout/${planId}${couponParam}`);
  };

  const currentRules = RULES_INFO[challengeType];
  const sizes = challengeType === 'instant' ? [5000, 10000, 25000, 50000, 100000] : [5000, 10000, 25000, 50000, 100000];

  return (
    <div className="min-h-screen bg-[#050505] bg-noise">
      <Navbar />

      <section className="pt-24 pb-16 relative">
        {/* Background orbs */}
        <div className="absolute top-20 left-0 w-[400px] h-[400px] bg-[#00D4FF]/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-20 right-0 w-[300px] h-[300px] bg-[#7B61FF]/5 rounded-full blur-[100px]" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            {/* Urgency Banner */}
            <div className="inline-flex items-center gap-2 mb-6 px-5 py-2.5 bg-gradient-to-r from-[#7B61FF]/10 to-[#00D4FF]/10 border border-white/10 rounded-full">
              <Tag className="w-4 h-4 text-brand-primary" />
              <span className="text-zinc-300 text-sm">Limited Time &mdash; Use <span className="text-brand-primary font-bold">AYRONN20</span> for 20% OFF</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-heading tracking-tighter text-white mb-4">
              Choose Your <span className="text-gradient-primary">Challenge</span>
            </h1>
            <p className="text-base text-zinc-400 max-w-2xl mx-auto">Professional trading challenges with strict risk management. Select the account size and challenge type that fits your style.</p>
          </motion.div>

          {/* Challenge Type Toggle */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex flex-wrap justify-center gap-1 p-1.5 bg-[#0A0F1C] border border-white/5 rounded-xl" data-testid="challenge-type-toggle">
              {['1-step', '2-step', 'instant'].map((type) => (
                <button key={type} onClick={() => setChallengeType(type)}
                  className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold transition-all duration-300 text-xs sm:text-sm ${challengeType === type ? 'bg-brand-primary text-black shadow-lg shadow-brand-primary/20' : 'text-zinc-400 hover:text-white'}`}
                  data-testid={`challenge-type-${type}`}>
                  {type === '2-step' ? '2-Step' : type === '1-step' ? '1-Step' : 'Instant'}
                </button>
              ))}
            </div>
          </div>

          {/* Coupon Input */}
          <div className="flex justify-center mb-4">
            <div className="flex flex-col sm:flex-row items-center gap-2">
              <input type="text" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} placeholder="Enter coupon code"
                className="px-4 py-2.5 bg-[#0A0F1C] border border-white/10 rounded-lg text-white text-sm focus:border-brand-primary focus:ring-1 focus:ring-brand-primary/50 outline-none w-full sm:w-48"
                data-testid="coupon-input" />
              <button onClick={handleApplyCoupon} className="px-5 py-2.5 bg-white/5 border border-white/10 text-white text-sm font-semibold rounded-lg hover:bg-white/10 transition-all" data-testid="apply-coupon-btn">
                Apply
              </button>
              {appliedCoupon && <span className="text-brand-positive text-sm font-semibold">{appliedCoupon.discount_pct}% OFF</span>}
            </div>
          </div>

          {/* Powered by Exness */}
          <div className="flex justify-center mb-10">
            <div className="flex items-center gap-2.5 px-5 py-2 bg-white/[0.04] border border-white/10 rounded-full" data-testid="pricing-exness-badge">
              <img src="/exness-logo.png" alt="Exness" className="h-5 w-auto" />
              <span className="text-zinc-300 text-sm font-medium">Powered by <span className="text-white font-bold">Exness</span> Simulated Accounts</span>
            </div>
          </div>

          {/* Trading Rules Card */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="mb-10 p-6 glass-heavy rounded-2xl border-l-4" style={{ borderLeftColor: currentRules.color }}>
            <div className="flex items-center gap-2 mb-4">
              <Info className="w-5 h-5 text-zinc-400" />
              <h3 className="text-lg font-bold text-white">{currentRules.label} &mdash; Trading Rules</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {currentRules.rules.map((rule, idx) => (
                <div key={idx} className="flex items-center gap-2.5 text-sm text-zinc-300">
                  <span className="text-zinc-500">{rule.icon}</span>
                  {rule.text}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Pricing Cards */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {[...Array(5)].map((_, i) => <div key={i} className="h-[380px] skeleton rounded-2xl" />)}
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div key={challengeType} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}
                className={`grid grid-cols-1 md:grid-cols-2 ${sizes.length <= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-5'} gap-6`} style={{overflow:'visible'}}>
                {sizes.map((size) => {
                  const plan = filteredPlans.find(p => p.account_size === size);
                  if (!plan) return null;
                  const isFeatured = (challengeType === '1-step' && size === 50000) || (challengeType === '2-step' && size === 25000) || (challengeType === 'instant' && size === 50000);
                  const isMostPopular = (challengeType === '1-step' && size === 10000) || (challengeType === '2-step' && size === 10000) || (challengeType === 'instant' && size === 25000);
                  const discounted = getDiscountedPrice(plan.price);
                  const hasDiscount = appliedCoupon && discounted < plan.price;

                  return (
                    <motion.div key={plan.id} layout
                      className={`relative rounded-2xl card-shine overflow-hidden ${isFeatured ? 'bg-gradient-to-b from-brand-primary/[0.08] to-transparent ring-2 ring-brand-primary/60 shadow-[0_0_35px_rgba(0,212,255,0.15)] z-10' : isMostPopular ? 'bg-gradient-to-b from-[#7B61FF]/[0.06] to-transparent ring-1 ring-[#7B61FF]/40 shadow-[0_0_25px_rgba(123,97,255,0.1)]' : 'bg-[#0A0F1C]/70 border border-white/5'} hover:-translate-y-2 transition-all duration-500 group`}
                      data-testid={`pricing-plan-${plan.account_size}`}>

                      {/* BEST VALUE Header Strip */}
                      {isFeatured && (
                        <div className="w-full py-2 bg-gradient-to-r from-brand-primary to-[#00B4D8] text-center shadow-[0_0_25px_rgba(0,212,255,0.5)]" data-testid="best-value-badge">
                          <span className="text-black text-[11px] sm:text-xs font-black uppercase tracking-[0.15em]">★ BEST VALUE ★</span>
                        </div>
                      )}

                      {/* MOST POPULAR Header Strip */}
                      {isMostPopular && (
                        <div className="w-full py-2 bg-gradient-to-r from-[#7B61FF] to-[#A78BFA] text-center shadow-[0_0_25px_rgba(123,97,255,0.5)]" data-testid="most-popular-badge">
                          <span className="text-white text-[11px] sm:text-xs font-black uppercase tracking-[0.15em]">★ MOST POPULAR ★</span>
                        </div>
                      )}

                      <div className="p-6 text-center">
                        <div className="text-xs text-zinc-500 uppercase tracking-wider mb-3">{plan.challenge_type === 'instant' ? 'Instant Funding' : `${plan.challenge_type} Challenge`}</div>
                        <div className="text-4xl font-black font-mono text-white mb-3">${(plan.account_size / 1000).toFixed(0)}K</div>

                        {hasDiscount ? (
                          <div className="mb-5">
                            <span className="text-zinc-500 line-through text-lg mr-2">${plan.price}</span>
                            <span className="text-brand-primary text-3xl font-bold">${discounted}</span>
                          </div>
                        ) : (
                          <div className="text-brand-primary text-3xl font-bold mb-5">${plan.price}</div>
                        )}

                        <div className="space-y-2.5 mb-6 text-left">
                          <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-positive flex-shrink-0 mt-0.5" /><span className="text-sm text-zinc-400">{plan.profit_split}% Profit Split</span></div>
                          {plan.profit_target > 0 && <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-positive flex-shrink-0 mt-0.5" /><span className="text-sm text-zinc-400">{plan.profit_target}% Profit Target</span></div>}
                          <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-positive flex-shrink-0 mt-0.5" /><span className="text-sm text-zinc-400">{plan.max_drawdown}% Max Drawdown</span></div>
                          <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-positive flex-shrink-0 mt-0.5" /><span className="text-sm text-zinc-400">{plan.daily_loss_limit}% Daily Loss Limit</span></div>
                          {plan.min_trading_days > 0 && <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-positive flex-shrink-0 mt-0.5" /><span className="text-sm text-zinc-400">{plan.min_trading_days} Min Trading Days</span></div>}
                        </div>

                        {/* Elite Salary potential — visible on every card */}
                        {(plan.elite_salary_potential || 0) > 0 && (
                          <div className="mb-5 -mx-1 p-3 rounded-xl bg-gradient-to-br from-[#D4AF37]/10 via-[#D4AF37]/[0.04] to-transparent border border-[#D4AF37]/25 text-left" data-testid={`elite-salary-${plan.account_size}`}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <Crown className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
                                <span className="text-[10px] uppercase tracking-wider font-black text-[#D4AF37]">Elite Salary</span>
                              </div>
                              <div className="text-[#D4AF37] font-black text-base font-mono leading-none">
                                ${(plan.elite_salary_potential).toLocaleString()}<span className="text-[9px] text-[#D4AF37]/70">/mo</span>
                              </div>
                            </div>
                            <div className="mt-1.5 text-[10px] text-zinc-500 leading-snug">
                              Unlocked after <strong className="text-[#D4AF37]">{plan.elite_payouts_required || 12} consistent payouts</strong> from this account.
                            </div>
                          </div>
                        )}

                        <button onClick={() => handleSelectPlan(plan.id)}
                          className={`w-full py-3 font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 group/btn ${isFeatured ? 'bg-brand-primary text-black hover:glow-primary' : isMostPopular ? 'bg-[#7B61FF] text-white hover:shadow-[0_0_20px_rgba(123,97,255,0.3)]' : 'bg-white/5 border border-white/10 text-white hover:bg-brand-primary hover:text-black hover:border-brand-primary'}`}
                          data-testid={`buy-now-${plan.account_size}`}>
                          Buy Now <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            </AnimatePresence>
          )}

          {/* Payout Info */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="glass-heavy rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">Payout Schedule</h3>
              <div className="space-y-3 text-sm text-zinc-400">
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-positive" /> <span className="text-brand-primary font-bold">Daily payouts</span> — our USP</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-positive" /> Request profits every day</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-positive" /> Up to 90% profit split</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-positive" /> Crypto payout supported</div>
              </div>
            </div>
            <div className="glass-heavy rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">All Plans Include</h3>
              <div className="space-y-3 text-sm text-zinc-400">
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-positive" /> Strict risk management engine</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-positive" /> Real-time violation tracking</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-positive" /> Professional trading dashboard</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-positive" /> 24/7 customer support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
